import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import LanguageSwitcher from './LanguageSwitcher'
import { useI18n } from '../lib/i18n'

interface NavbarMobileProps {
  isVisible: boolean
}

export default function NavbarMobile({ isVisible }: NavbarMobileProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useI18n()

  const menuVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 }
    }
  }

  const linkVariants = {
    hover: {
      color: '#00BCC5',
      y: -2,
      transition: { duration: 0.2 }
    }
  }

  return (
    <>
      {/* Mobile Header: Floating Card Style */}
      <motion.nav
        className="nav-container fixed top-[2vh] left-[3vw] right-[3vw] z-50 bg-white rounded-lg shadow-md h-[8vh] flex items-center justify-between px-[4vw]"
        initial={{ y: -20, opacity: 0 }}
        animate={{
          y: isVisible ? 0 : -100,
          opacity: isVisible ? 1 : 0
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* LOGO */}
        <a href="/">
          <motion.img
            src="/Logo.png"
            alt="Park-Box Frankfurt"
            crossOrigin="anonymous"
            className="logo h-[5.5vh] object-contain"
            whileHover={{ scale: 1.05 }}
          />
        </a>

        <div className="flex items-center gap-3">
          {/* Language Switcher */}
          <LanguageSwitcher variant="mobile" />

          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className="hamburger-btn p-1"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <img src="https://api.iconify.design/lucide/menu.svg?color=%23172B4D&height=28" alt="Menu" className="w-[3.5vh] h-[3.5vh]" />
          </motion.button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mobile-menu-overlay fixed inset-0 bg-black/95 z-[60] flex flex-col items-center justify-center gap-[4vh] backdrop-blur-sm"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={menuVariants}
          >
            <motion.button
              onClick={() => setIsOpen(false)}
              className="absolute top-[3vh] right-[5vw] p-2 bg-white/10 rounded-full"
              whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.1)" }}
              whileTap={{ scale: 0.9 }}
            >
              <img src="https://api.iconify.design/lucide/x.svg?color=%23ffffff&height=28" alt="Close" className="w-[3.5vh] h-[3.5vh]" />
            </motion.button>

            {/* Nav Links */}
            <motion.a
              href="/valet-parken-frankfurt-flughafen"
              onClick={() => setIsOpen(false)}
              className="text-[3vh] text-white font-medium"
              variants={linkVariants}
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
            >
              {t.nav.parking}
            </motion.a>
            <motion.a
              href="/flughafen-frankfurt-preise"
              onClick={() => setIsOpen(false)}
              className="text-[3vh] text-white font-medium"
              variants={linkVariants}
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
            >
              {t.nav.prices}
            </motion.a>
            <motion.a
              href="/fragen-antworten-flughafenparkplatz-frankfurt"
              onClick={() => setIsOpen(false)}
              className="text-[3vh] text-white font-medium"
              variants={linkVariants}
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
            >
              {t.nav.faq}
            </motion.a>
            <motion.a
              href="/parkplatz-hattersheim-frankfurt-flughafen-anfahrt"
              onClick={() => setIsOpen(false)}
              className="text-[3vh] text-white font-medium"
              variants={linkVariants}
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
            >
              {t.nav.directions}
            </motion.a>

            <motion.a
              href="/buchen"
              onClick={() => setIsOpen(false)}
              className="mt-[2vh] bg-[#0052CC] text-white px-[8vw] py-[1.5vh] rounded-full text-[2.5vh] font-bold"
              whileHover={{
                scale: 1.05,
                backgroundColor: '#0747A6',
                boxShadow: "0px 10px 20px rgba(0, 82, 204, 0.3)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              {t.nav.bookNow}
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
